import { Fragment, useEffect, useRef, useState } from 'react';
import type { OutreachFillStepRequest, OutreachFillStepResult, OutreachTemplateGroups } from '@discovr/types';
import { Plus } from '../../../../design-system/icons';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import type { ComposerEditorHandle } from '../composerEditor/ComposerEditor';
import { composerHtmlFromText, composerTextFromHtml } from '../composerEditor/headless';
import { DEFAULT_TEMPLATE_LABEL, SCOPE_LABEL, TEMPLATE_LOAD_ERROR_COPY, defaultSequenceTemplate, templateSections } from '../defaultSequenceTemplate';
import { PriorStepsEmptyError } from '../templateFillError';
import { TemplateStepEditor, type TemplateStepField } from '../TemplateStepEditor';
import { FOLLOW_UP_CAP_NOTE, MAX_STEPS, emptyFollowUpStep, toStepDrafts, type TemplateStepDraft } from '../templateValidation';
import { resolveTemplate, stepsMatchTemplate } from './batchTemplate';
import { WizardStepHeader } from './WizardStepHeader';
import type { WizardAction, WizardState } from './wizardState';

const MAX_DELAY_DAYS = 365;

export const TEMPLATE_STEP_SUB = "Every creator's email is written from this template. Edit it here; it is saved to this campaign's templates when drafting starts."; // copy: ours
export const SAVED_AS_NEW_COPY = 'Your edits are saved as a new template for this campaign. The original is not changed.'; // copy: ours
export const SAVED_AS_VERSION_COPY = 'Your edits are saved as a new version of this template.'; // copy: ours

/** A step's formatted body as the AI route wants it: the editor's HTML, or the plain text wrapped the same way. */
const bodyHtmlOf = (step: TemplateStepDraft): string => step.bodyHtml || composerHtmlFromText(step.body);

export interface TemplateStepProps {
  state: WizardState;
  dispatch: (action: WizardAction) => void;
  groups: OutreachTemplateGroups | undefined;
  loadError: boolean;
  /** Asks the fill-step route for one step; rejects when the AI fails. */
  onFillStep: (req: OutreachFillStepRequest) => Promise<OutreachFillStepResult>;
  /** Why saving the template or starting the drafts failed. */
  error?: string | null;
}

/** Step 2: pick this batch's template, edit it, add follow-ups (max 5), and have the AI write any step. */
export function TemplateStep({ state, dispatch, groups, loadError, onFillStep, error }: TemplateStepProps) {
  const steps = state.templateSteps;
  const template = resolveTemplate(groups, state.templateId);

  // The working copy starts as the picked template's own steps; a pick of another template resets it.
  useEffect(() => {
    if (template && state.templateSteps === null) dispatch({ type: 'templateSteps', steps: toStepDrafts(template.steps) });
  }, [template, state.templateSteps, dispatch]);

  // A stable id per step slot, so a delete or duplicate never hands one card another card's local wait-picker state.
  const [stepKeys, setStepKeys] = useState<string[]>([]);
  if (steps && stepKeys.length !== steps.length) {
    setStepKeys(steps.map((_, i) => stepKeys[i] ?? crypto.randomUUID()));
  }
  const [bodySeeds, setBodySeeds] = useState<Record<string, number>>({});
  const bodyHandles = useRef(new Map<string, ComposerEditorHandle>());
  const caretRef = useRef<{ index: number; field: TemplateStepField; start: number; end: number } | null>(null);
  // The latest steps and keys, so an AI answer that lands later finds its step by identity, not by a position that may have moved.
  const latest = useRef({ steps, stepKeys });
  useEffect(() => {
    latest.current = { steps, stepKeys };
  });

  if (loadError) {
    return (
      <div className="mx-auto w-full max-w-[880px] space-y-5">
        <WizardStepHeader heading="Template" sub={TEMPLATE_STEP_SUB} />
        <p role="alert" className="text-body-sm-regular text-amber-700">{TEMPLATE_LOAD_ERROR_COPY}</p>
      </div>
    );
  }

  const sections = groups ? templateSections(groups) : [];
  const defaultTemplate = groups ? defaultSequenceTemplate(groups) : undefined;
  const pinnedGlobal = template && template.scope === 'global' && template.id !== defaultTemplate?.id ? template : undefined;
  const setSteps = (next: TemplateStepDraft[]) => dispatch({ type: 'templateSteps', steps: next.map((s, i) => ({ ...s, stepNo: i + 1 })) });
  const patchStep = (index: number, patch: Partial<TemplateStepDraft>) => {
    if (steps) setSteps(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  };
  const atCap = (steps?.length ?? 0) >= MAX_STEPS;
  const addFollowUp = () => {
    if (!steps || atCap) return;
    setStepKeys([...stepKeys, crypto.randomUUID()]);
    setSteps([...steps, emptyFollowUpStep(steps.length + 1)]);
  };
  const duplicate = (index: number) => {
    const source = steps?.[index];
    if (!steps || !source || atCap) return;
    caretRef.current = null;
    setStepKeys([...stepKeys.slice(0, index + 1), crypto.randomUUID(), ...stepKeys.slice(index + 1)]);
    setSteps([...steps.slice(0, index + 1), { ...source, stepNo: index + 2 }, ...steps.slice(index + 1)]);
  };
  const remove = (index: number) => {
    if (!steps) return;
    caretRef.current = null;
    setStepKeys(stepKeys.filter((_, i) => i !== index));
    setSteps(steps.filter((_, i) => i !== index));
  };

  const insertTag = (index: number, field: TemplateStepField, tag: string) => {
    if (!steps) return;
    if (field === 'body') {
      bodyHandles.current.get(stepKeys[index] ?? '')?.insertTextAtCursor(tag);
      return;
    }
    const at = caretRef.current?.index === index && caretRef.current.field === 'subject' ? caretRef.current : null;
    const subject = steps[index]?.subject ?? '';
    const start = at?.start ?? subject.length;
    const end = at?.end ?? subject.length;
    patchStep(index, { subject: `${subject.slice(0, start)}${tag}${subject.slice(end)}` });
    caretRef.current = { index, field: 'subject', start: start + tag.length, end: start + tag.length };
  };

  // The AI writes one step from the steps before it. A failure rejects and the step is left exactly as it was.
  const fillStep = async (index: number, instruction: string) => {
    const step = steps?.[index];
    if (!steps || !step) return;
    const emptyBefore = steps.slice(0, index).findIndex((prior) => !prior.body.trim());
    if (emptyBefore >= 0) throw new PriorStepsEmptyError(emptyBefore + 2);
    const delay = Number(step.delayDays);
    const hasDelay = index > 0 && step.delayDays.trim() !== '' && Number.isFinite(delay) && delay >= 0 && delay <= MAX_DELAY_DAYS;
    const result = await onFillStep({
      stepNo: index + 1,
      stepsTotal: steps.length,
      ...(index === 0 && step.subject.trim() ? { subject: step.subject } : {}),
      ...(step.body.trim() ? { bodyHtml: bodyHtmlOf(step) } : {}),
      ...(instruction ? { instruction } : {}),
      ...(hasDelay ? { delayDays: delay } : {}),
      priorSteps: steps.slice(0, index).map((prior, i) => ({
        ...(i === 0 && prior.subject.trim() ? { subject: prior.subject } : {}),
        bodyHtml: bodyHtmlOf(prior),
      })),
    });
    const key = stepKeys[index] ?? '';
    const { steps: now, stepKeys: keys } = latest.current;
    const current = keys.indexOf(key);
    if (!now || current < 0) return;
    setSteps(now.map((s, i) => (i === current ? { ...s, body: composerTextFromHtml(result.bodyHtml), bodyHtml: result.bodyHtml, aiAssisted: true, ...(current === 0 && result.subject ? { subject: result.subject } : {}) } : s)));
    setBodySeeds((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
  };

  const edited = !!steps && !!template && !stepsMatchTemplate(steps, template);
  const editNote = edited && template ? (template.scope === 'campaign' ? SAVED_AS_VERSION_COPY : SAVED_AS_NEW_COPY) : null;
  const selectId = 'wizard-template-select';

  return (
    <div className="mx-auto w-full max-w-[880px] space-y-5">
      <WizardStepHeader heading="Template" sub={TEMPLATE_STEP_SUB} />
      {groups ? (
        <div className="space-y-1.5">
          <Label htmlFor={selectId}>Template</Label>
          <select
            id={selectId}
            value={template ? String(template.id) : ''}
            onChange={(event) => dispatch({ type: 'template', templateId: Number(event.target.value) })}
            className="h-9 w-full max-w-sm rounded-md border border-border bg-background px-3 text-body-sm-regular"
          >
            {template ? null : <option value="">Built-in default message</option>}
            {defaultTemplate ? <option value={String(defaultTemplate.id)}>{DEFAULT_TEMPLATE_LABEL}</option> : null}
            {pinnedGlobal ? <option value={String(pinnedGlobal.id)}>{pinnedGlobal.name}</option> : null}
            {sections.map((section) =>
              section.rows.length === 0 ? null : (
                <optgroup key={section.scope} label={SCOPE_LABEL[section.scope]}>
                  {section.rows.map((t) => (
                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                  ))}
                </optgroup>
              ),
            )}
          </select>
        </div>
      ) : (
        <p className="text-body-sm-regular text-muted-foreground">Loading templates…</p>
      )}
      {error ? <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-body-sm-regular text-rose-700">{error}</p> : null}
      {editNote ? <p role="status" className="text-body-sm-regular text-muted-foreground">{editNote}</p> : null}
      {steps ? (
        <div className="flex flex-col gap-4">
          {steps.map((step, index) => (
            <Fragment key={stepKeys[index] ?? index}>
              <TemplateStepEditor
                index={index}
                step={step}
                solo={steps.length === 1}
                isLastStep={index === steps.length - 1 && steps.length >= 3}
                canDuplicate={!atCap}
                onChange={(patch) => patchStep(index, patch)}
                onAiFill={(instruction) => fillStep(index, instruction)}
                onDuplicate={() => duplicate(index)}
                onDelete={index > 0 ? () => remove(index) : undefined}
                onCaret={(field, el) => {
                  const fallback = el.value.length;
                  caretRef.current = { index, field, start: el.selectionStart ?? fallback, end: el.selectionEnd ?? fallback };
                }}
                onInsertTag={(field, tag) => insertTag(index, field, tag)}
                bodyHandle={(handle) => {
                  const key = stepKeys[index] ?? String(index);
                  if (handle) bodyHandles.current.set(key, handle);
                  else bodyHandles.current.delete(key);
                }}
                bodySeed={bodySeeds[stepKeys[index] ?? ''] ?? 0}
                subjectHelp="Tidied once by AI for the whole campaign; tags like {creator_name} fill in per creator."
              />
            </Fragment>
          ))}
          <div className="flex flex-col items-center gap-2">
            <Button type="button" variant="outline" className="rounded-full" disabled={atCap} onClick={addFollowUp}>
              <Plus size={16} />
              Add a follow-up
            </Button>
            {atCap ? <p className="text-caption-regular text-muted-foreground">{FOLLOW_UP_CAP_NOTE}</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
